import { z } from "zod";
import { isoDate } from "./dates";

export const UTILITIES = ["ELECTRICITY", "GAS", "WATER", "HOT_WATER", "HEAT", "COOLING"] as const;
export type UtilityCode = (typeof UTILITIES)[number];
export const UTILITY_META: Record<UtilityCode, { label: string; unit: string; decimals: number; emoji: string }> = {
  ELECTRICITY: { label: "Villany", unit: "kWh", decimals: 1, emoji: "⚡" },
  GAS: { label: "Gáz", unit: "m³", decimals: 3, emoji: "🔥" },
  WATER: { label: "Víz", unit: "m³", decimals: 3, emoji: "💧" },
  HOT_WATER: { label: "Melegvíz", unit: "m³", decimals: 3, emoji: "♨️" },
  HEAT: { label: "Hőmennyiség", unit: "GJ", decimals: 3, emoji: "🌡️" },
  COOLING: { label: "Hűtés", unit: "kWh", decimals: 1, emoji: "❄️" },
};

export const UTILITY_MODE_LABEL = {
  SUBMETERED: "Almérő szerint továbbszámlázva",
  DIRECT: "A bérlő nevén (nem számlázzuk)",
  ALLOCATED: "Üzemeltetési díjban, terület szerint",
} as const;
export const READING_SOURCE_LABEL = { MANUAL: "Kézi", PHOTO: "Fotó", TENANT: "Bérlői bejelentés", PROVIDER: "Szolgáltatói számla", REMOTE: "Távleolvasás" } as const;
export const READING_KIND_LABEL = { REGULAR: "Leolvasás", INITIAL: "Nyitó állás", FINAL: "Záró állás" } as const;

const optStr = (max = 200) => z.string().trim().max(max).nullable().optional();
const num = z.coerce.number({ invalid_type_error: "Számot adj meg" });

export const meterSchema = z.object({
  utility: z.enum(UTILITIES),
  serialNo: z.string().trim().min(1, "Add meg a gyári számot").max(60),
  name: optStr(120),
  multiplier: num.positive().max(100000).default(1),
  accountHolder: z.enum(["LANDLORD", "TENANT"]).default("LANDLORD"),
  parentMeterId: z.string().uuid().nullable().optional(),
  provider: optStr(120),
  podCode: optStr(60),
  location: optStr(200),
  note: optStr(1000),
});
export type MeterInput = z.infer<typeof meterSchema>;

export const newMeterSchema = meterSchema.extend({
  installedAt: isoDate,
  initialValue: num.min(0).default(0),
  spaceIds: z.array(z.string().uuid()).default([]),
});

export const readingSchema = z.object({
  date: isoDate,
  value: num.min(0).max(1e12),
  source: z.enum(["MANUAL", "PHOTO", "TENANT", "PROVIDER", "REMOTE"]).default("MANUAL"),
  isEstimate: z.boolean().default(false),
  note: optStr(500),
  /** Visszaforduló / csökkenő állás tudatos rögzítése (pl. hibás korábbi érték) */
  allowDecrease: z.boolean().default(false),
});

export const bulkReadingsSchema = z.object({
  date: isoDate,
  readings: z.array(z.object({ meterId: z.string().uuid(), value: num.min(0).max(1e12) })).min(1).max(500),
});

export const voidReadingSchema = z.object({ reason: z.string().trim().min(3, "Írd le, miért érvénytelen").max(500) });

export const assignMeterSchema = z.object({
  effectiveDate: isoDate,
  spaceIds: z.array(z.string().uuid()),
  /** Opcionális köztes leolvasás az átkerülés napján – ettől pontos a megosztás. */
  reading: num.min(0).nullable().optional(),
});

export const replaceMeterSchema = z.object({
  date: isoDate,
  finalValue: num.min(0),
  reason: optStr(500),
  newMeter: meterSchema.partial({ utility: true }).extend({ initialValue: num.min(0).default(0) }),
});

export const removeMeterSchema = z.object({ date: isoDate, finalValue: num.min(0), reason: optStr(500) });

export const utilityPriceSchema = z.object({
  utility: z.enum(UTILITIES),
  effectiveDate: isoDate,
  purchasePrice: num.min(0).max(1e6),
  rechargePrice: num.min(0).max(1e6),
});

export const leaseUtilityTermSchema = z.object({
  effectiveDate: isoDate,
  utility: z.enum(UTILITIES),
  mode: z.enum(["SUBMETERED", "DIRECT", "ALLOCATED"]),
  priceSource: z.enum(["PROPERTY", "FIXED"]).default("PROPERTY"),
  fixedPrice: num.min(0).max(1e6).nullable().optional(),
});
